import ContentShell from "components/ContentShell";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Grid, Typography } from "@mui/material";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import { makeStyles } from "@mui/styles";
import { SimpleTextInput } from "components/FormTextInput";
import { SimpleSelectInput } from "components/FormSelectInput";
import {
  createOperatorGame,
  getOperatorGameInfo,
  updateOperatorGame,
} from "services/operatorGameService";
import { getGamesList } from "services/gameService";
import sessionHelper from "services/sessionHelper";
import SimpleSwitch from "components/Switch";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
    maxWidth: "1200px",
  },
  section: {
    marginBottom: "32px",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: 600,
    marginBottom: "16px",
    color: theme.palette.primary.main,
  },
  field: {
    marginBottom: "16px",
  },
  buttonContainer: {
    display: "flex",
    gap: "16px",
    justifyContent: "flex-end",
  },
  infoText: {
    fontSize: "12px",
    color: theme.palette.text.secondary,
    marginTop: "4px",
  },
}));

const _operatorGameDetails = {
  platform: "",
  operator: "",
  brand: "",
  game: "",
  launchPath: "",
  icon: "",
  isActive: true,
};

const OperatorGamesEditor = () => {
  const classes = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [operatorGameDetails, setOperatorGameDetails] = useState(_operatorGameDetails);
  const [gameOptions, setGameOptions] = useState([]);
  const isEditMode = id !== "create";

  // Get filter options from sessionHelper
  const platformOptions = sessionHelper.platformOptions || [];
  const operatorOptions = sessionHelper.operatorOptions || [];
  const brandOptions = sessionHelper.brandOptions || [];

  const cancel = () => navigate(configData.routes.operatorGames.list);

  const validateForm = () => {
    if (!operatorGameDetails.platform) {
      showSnackbar("Platform is required", "error");
      return false;
    }
    if (!operatorGameDetails.operator) {
      showSnackbar("Operator is required", "error");
      return false;
    }
    if (!operatorGameDetails.brand) {
      showSnackbar("Brand is required", "error");
      return false;
    }
    if (!operatorGameDetails.game) {
      showSnackbar("Game is required", "error");
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      let response;

      // Prepare payload - convert empty strings to undefined
      const payload: any = { ...operatorGameDetails };
      Object.keys(payload).forEach(key => {
        if (payload[key] === "") {
          delete payload[key];
        }
      });

      if (isEditMode) {
        const { _id, id, updatedAt, createdAt, platform, operator, brand, gameAlias, maxBet, minBet, maxWin, defaultBet, gameMode, ...updateData } = payload;
        response = await updateOperatorGame(_id, updateData);
      } else {
        response = await createOperatorGame(payload);
      }

      showSnackbar(response.message || "Operator game saved successfully", "success");
      setLoading(false);
      navigate(configData.routes.operatorGames.list);
    } catch (error) {
      showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const onChange = (e: any) => {
    const { name, value } = e?.target;
    setOperatorGameDetails((state) => ({
      ...state,
      [name]: name === "isActive" ? !state.isActive : value,
    }));
  };

  const fetchGamesList = async () => {
    try {
      const resp = await getGamesList({ skipPagination: true });
      // Handle different possible response structures
      const games = resp?.data?.platforms || resp?.data?.data || resp?.data || [];
      setGameOptions(
        games.map((game) => ({
          id: game._id,
          label: game.gameName,
        }))
      );
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const fetchOperatorGameInfo = async () => {
    try {
      setLoading(true);
      const resp = await getOperatorGameInfo(id);
      const data = resp.data;

      // Handle populated references
      setOperatorGameDetails({
        ...data,
        platform: data.platform?._id || data.platform,
        operator: data.operator?._id || data.operator,
        brand: data.brand?._id || data.brand,
        game: data.game?._id || data.game,
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  useEffect(() => {
    fetchGamesList();
    if (isEditMode) fetchOperatorGameInfo();
  }, []);

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={`${isEditMode ? "Edit" : "Create"} Operator Game`}
      loading={loading}
      actions={
        <Box className={classes.buttonContainer}>
          <Button
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={submit} disabled={loading}>
            Save
          </Button>
        </Box>
      }
    >
      <Box className={classes.container}>
        {/* Basic Information Section */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Basic Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box className={classes.field}>
                <SimpleSelectInput
                  label="Platform *"
                  name="platform"
                  value={operatorGameDetails.platform}
                  onChange={onChange}
                  options={platformOptions}
                  disabled={isEditMode}
                />
                {isEditMode && (
                  <Typography className={classes.infoText}>
                    Platform cannot be changed after creation
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.field}>
                <SimpleSelectInput
                  label="Operator *"
                  name="operator"
                  value={operatorGameDetails.operator}
                  onChange={onChange}
                  options={operatorOptions}
                  disabled={isEditMode}
                />
                {isEditMode && (
                  <Typography className={classes.infoText}>
                    Operator cannot be changed after creation
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.field}>
                <SimpleSelectInput
                  label="Brand *"
                  name="brand"
                  value={operatorGameDetails.brand}
                  onChange={onChange}
                  options={brandOptions}
                  disabled={isEditMode}
                />
                {isEditMode && (
                  <Typography className={classes.infoText}>
                    Brand cannot be changed after creation
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.field}>
                <SimpleSelectInput
                  label="Game *"
                  name="game"
                  value={operatorGameDetails.game}
                  onChange={onChange}
                  options={gameOptions}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Game Configuration Section */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Game Configuration
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box className={classes.field}>
                <SimpleTextInput
                  label="Launch Path"
                  name="launchPath"
                  value={operatorGameDetails.launchPath}
                  onChange={onChange}
                  placeholder="Enter launch path (optional)"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.field}>
                <SimpleTextInput
                  label="Icon URL"
                  name="icon"
                  value={operatorGameDetails.icon}
                  onChange={onChange}
                  placeholder="Enter icon URL (optional)"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Status Section */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Status</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box className={classes.field}>
                <SimpleSwitch
                  label="Active"
                  name="isActive"
                  value={operatorGameDetails.isActive}
                  onChange={onChange}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </ContentShell>
  );
};

export default OperatorGamesEditor;
